'use strict';

/**
 * Pippei tooltips («toggletips»): one shared popover for every [data-tip] trigger.
 *
 * - PC (mouse): opens on hover after 150 ms; the pointer may move into the box (links stay clickable).
 *   Click pins it open, a second click closes it.
 * - Mobile/touch: tap opens, tap outside, the close button or scrolling closes.
 * - Keyboard: Enter/Space opens, Tab moves into the box, Esc closes and returns focus.
 * - Placement: below the trigger, above when there is more room there; always clamped inside the screen
 *   (12 px gutter, 16 px and full width on phones), arrow follows the trigger, long text scrolls inside.
 * - The popover lives in the top layer (Popover API), so overflow:hidden on cards, carousels and the
 *   cart drawer never clips it. Browsers without the API get the same box with position:fixed.
 *
 * Content: <template data-tip-content> inside the trigger (rendered by snippets/tooltip.liquid), or a handle
 * of the metaobject «Infotekst» looked up in the registry /?view=infotekst (cached for 10 minutes per tab).
 * Links in rich text become triggers: <a href="#infotekst-plast-plastfritt">plastfritt</a>, or any element
 * with data-infotekst="plast-plastfritt".
 */
(function () {
  if (window.PippeiTip) return;

  var strings = window.PippeiTipStrings || { close: 'Lukk', loading: 'Laster…', error: 'Fant ikke forklaringen.' };
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var REGISTRY_URL = root + '?view=infotekst';
  var CACHE_KEY = 'pippei-infotekst', CACHE_TTL = 10 * 60 * 1000;
  var OPEN_DELAY = 150, CLOSE_DELAY = 200, GAP = 10;
  var hasPopover = typeof HTMLElement.prototype.showPopover === 'function';

  var tip = null, scroller = null, closeBtn = null;
  var current = null, pinned = false, openTimer = null, closeTimer = null;
  var registry = null, registryRequest = null;

  /* ---------- content ---------- */

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  }

  function loadRegistry() {
    if (registry) return Promise.resolve(registry);
    try {
      var cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.t < CACHE_TTL) { registry = cached.items; return Promise.resolve(registry); }
    } catch (e) { /* storage blocked: fetch instead */ }
    if (!registryRequest) {
      registryRequest = fetch(REGISTRY_URL, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
        .then(function (text) {
          registry = (JSON.parse(text.trim()).items) || {};
          try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), items: registry })); } catch (e) { /* ignore */ }
          return registry;
        })
        .catch(function (e) { registryRequest = null; throw e; });
    }
    return registryRequest;
  }

  function inlineContent(trigger) {
    var t = trigger.querySelector('template[data-tip-content]');
    return t ? t.innerHTML : null;
  }

  /* ---------- box ---------- */

  function build() {
    tip = document.createElement('div');
    tip.className = 'pp-tip';
    tip.id = 'PippeiTip';
    tip.setAttribute('role', 'dialog');
    if (hasPopover) tip.setAttribute('popover', 'manual'); else tip.hidden = true;
    tip.innerHTML =
      '<button type="button" class="pp-tip__close"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5" aria-hidden="true"><path d="M18 6l-12 12"/><path d="M6 6l12 12"/></svg></button>' +
      '<div class="pp-tip__scroll"></div>';
    scroller = tip.querySelector('.pp-tip__scroll');
    closeBtn = tip.querySelector('.pp-tip__close');
    closeBtn.setAttribute('aria-label', strings.close);
    closeBtn.addEventListener('click', function () { close(true); });
    tip.addEventListener('pointerenter', function () { clearTimeout(closeTimer); });
    tip.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse' && !pinned) scheduleClose(); });
    tip.addEventListener('keydown', onTipKey);
    document.body.appendChild(tip);
  }

  function isShown() { return hasPopover ? tip.matches(':popover-open') : !tip.hidden; }
  function show() { if (!isShown()) { if (hasPopover) tip.showPopover(); else tip.hidden = false; } }
  function hide() { if (tip && isShown()) { if (hasPopover) tip.hidePopover(); else tip.hidden = true; } }

  function setContent(html) {
    scroller.innerHTML = html;
    scroller.scrollTop = 0;
    position();
  }

  function open(trigger, pin) {
    if (!tip) build();
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    if (current && current !== trigger) close(false);
    current = trigger;
    pinned = pinned || !!pin;
    trigger.setAttribute('aria-expanded', 'true');
    tip.setAttribute('aria-label', (trigger.getAttribute('aria-label') || trigger.textContent || '').trim());

    var html = inlineContent(trigger);
    if (html !== null) { show(); setContent(html); return; }

    var handle = trigger.getAttribute('data-infotekst');
    show();
    setContent('<p class="pp-tip__muted">' + escapeHtml(strings.loading) + '</p>');
    loadRegistry()
      .then(function (items) {
        if (current !== trigger) return;
        setContent(items[handle] || '<p class="pp-tip__muted">' + escapeHtml(strings.error) + '</p>');
      })
      .catch(function () {
        if (current === trigger) setContent('<p class="pp-tip__muted">' + escapeHtml(strings.error) + '</p>');
      });
  }

  function close(returnFocus) {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    if (!current) return;
    var trigger = current;
    current = null;
    pinned = false;
    trigger.setAttribute('aria-expanded', 'false');
    hide();
    if (returnFocus && document.contains(trigger)) trigger.focus({ preventScroll: true });
  }

  function scheduleClose() {
    clearTimeout(closeTimer);
    closeTimer = setTimeout(function () { if (!pinned) close(false); }, CLOSE_DELAY);
  }

  /* Below the trigger, or above when that fits better; clamped inside the viewport. */
  function position() {
    if (!current || !tip) return;
    if (!document.contains(current)) { close(false); return; }   // trigger re-rendered (cart drawer)
    var r = current.getBoundingClientRect();
    var vw = document.documentElement.clientWidth, vh = window.innerHeight;
    if (r.bottom < 0 || r.top > vh) { close(false); return; }
    var phone = vw < 640, gutter = phone ? 16 : 12;

    tip.classList.toggle('pp-tip--phone', phone);
    tip.style.width = phone ? (vw - 2 * gutter) + 'px' : '';
    var below = vh - r.bottom - GAP - gutter, above = r.top - GAP - gutter;
    var room = Math.max(below, above);
    scroller.style.maxHeight = Math.max(96, phone ? Math.min(vh * 0.5, room) : room) + 'px';

    var tw = tip.offsetWidth, th = tip.offsetHeight;
    var placeBelow = th <= below || below >= above;
    var top = placeBelow ? r.bottom + GAP : r.top - GAP - th;
    var cx = r.left + r.width / 2;
    var left = Math.min(Math.max(cx - tw / 2, gutter), vw - gutter - tw);
    tip.style.left = Math.round(left) + 'px';
    tip.style.top = Math.round(top) + 'px';
    tip.setAttribute('data-side', placeBelow ? 'bottom' : 'top');
    tip.style.setProperty('--pp-tip-arrow', Math.round(Math.min(Math.max(cx - left, 16), tw - 16)) + 'px');
  }

  function focusables() {
    return Array.prototype.slice.call(tip.querySelectorAll('a[href], button:not([disabled])')).filter(function (el) {
      return el.offsetParent !== null;
    });
  }

  function onTipKey(e) {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(true); return; }
    if (e.key !== 'Tab') return;
    var f = focusables();
    if (!f.length) return;
    if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); current.focus(); }
    else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); close(true); }
  }

  /* ---------- triggers in rich text ---------- */

  function upgrade(scope) {
    (scope || document).querySelectorAll('a[href^="#infotekst-"], [data-infotekst]:not([data-tip])').forEach(function (el) {
      if (el.hasAttribute('data-tip')) return;
      var handle = el.getAttribute('data-infotekst') || el.getAttribute('href').slice('#infotekst-'.length);
      if (!handle) return;
      el.setAttribute('data-infotekst', handle);
      el.setAttribute('data-tip', '');
      el.setAttribute('role', 'button');
      el.setAttribute('aria-expanded', 'false');
      if (!el.hasAttribute('href') && !el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
      el.classList.add('pp-tip-inline');
    });
  }

  /* ---------- events ---------- */

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-tip]');
    if (trigger) {
      e.preventDefault();
      if (trigger === current && pinned) close(false); else open(trigger, true);
      return;
    }
    if (current && tip && !tip.contains(e.target)) close(false);
  });

  document.addEventListener('pointerover', function (e) {
    if (e.pointerType !== 'mouse') return;
    var trigger = e.target.closest && e.target.closest('[data-tip]');
    if (!trigger) return;
    if (trigger === current) { clearTimeout(closeTimer); return; }
    if (pinned) return;
    clearTimeout(openTimer);
    openTimer = setTimeout(function () { open(trigger, false); }, OPEN_DELAY);
  });

  document.addEventListener('pointerout', function (e) {
    if (e.pointerType !== 'mouse') return;
    var trigger = e.target.closest && e.target.closest('[data-tip]');
    if (!trigger || trigger.contains(e.relatedTarget)) return;
    clearTimeout(openTimer);
    if (trigger === current && !pinned && !(tip && tip.contains(e.relatedTarget))) scheduleClose();
  });

  document.addEventListener('keydown', function (e) {
    if (!current) {
      var el = e.target;
      if ((e.key === 'Enter' || e.key === ' ') && el.matches && el.matches('[data-tip][tabindex]:not(a):not(button)')) {
        e.preventDefault();
        open(el, true);
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopImmediatePropagation();   // e.g. the cart drawer stays open; only the tooltip closes
      close(true);
      return;
    }
    if (e.key === 'Tab' && document.activeElement === current) {
      var f = focusables();
      if (f.length && !e.shiftKey) { e.preventDefault(); f[0].focus(); } else close(false);
    }
  });

  /* Phones: scrolling the page closes the box; PC: it follows the trigger. */
  document.addEventListener('scroll', function (e) {
    if (!current || (tip && tip.contains(e.target))) return;
    if (document.documentElement.clientWidth < 640) close(false);
    else requestAnimationFrame(position);
  }, { capture: true, passive: true });

  window.addEventListener('resize', function () { if (current) requestAnimationFrame(position); });

  upgrade(document);
  document.addEventListener('cart:updated', function () { upgrade(document); });

  /* Warm the registry when the page has handle-only triggers, so the first tap on mobile is instant. */
  if (document.querySelector('[data-infotekst]')) {
    (window.requestIdleCallback || function (fn) { setTimeout(fn, 1500); })(function () { loadRegistry().catch(function () {}); });
  }

  window.PippeiTip = { open: open, close: close, upgrade: upgrade };
})();
